<template>
  <div class="help-center">
    <div class="help-container">
      <!-- 侧边导航 -->
      <aside class="help-sidebar">
        <div class="sidebar-header">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span>帮助中心</span>
        </div>
        <div class="sidebar-nav">
          <div
            v-for="item in navItems"
            :key="item.key"
            class="nav-item"
            :class="{ active: currentTab === item.key }"
            @click="currentTab = item.key"
          >
            <span class="nav-icon" v-html="item.icon"></span>
            <span class="nav-text">{{ item.text }}</span>
          </div>
        </div>
      </aside>

      <!-- 内容区 -->
      <main class="help-content">
        <!-- 常见问题 -->
        <div v-if="currentTab === 'faq'" class="content-section">
          <h2 class="content-title">常见问题</h2>
          <div class="faq-list">
            <div class="faq-item" v-for="(item, i) in faqList" :key="i">
              <div class="faq-q" @click="toggleFaq(i)">
                <span class="faq-q-text">{{ item.q }}</span>
                <span class="faq-arrow" :class="{ open: item.open }">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </span>
              </div>
              <div class="faq-a" v-show="item.open">{{ item.a }}</div>
            </div>
          </div>
        </div>

        <!-- 直播指南 -->
        <div v-if="currentTab === 'live'" class="content-section">
          <h2 class="content-title">直播指南</h2>
          <div class="obs-download-card">
            <div>
              <strong>OBS Studio（Windows）</strong>
              <span>使用官方版本推送 RTMP 直播画面</span>
            </div>
            <a href="https://obsproject.com/download" target="_blank" rel="noopener noreferrer">前往 OBS 官网下载</a>
          </div>
          <div class="guide-list">
            <div class="guide-card" v-for="(step, i) in liveSteps" :key="i">
              <div class="guide-num">{{ i + 1 }}</div>
              <div class="guide-info">
                <div class="guide-title">{{ step.title }}</div>
                <div class="guide-desc">{{ step.desc }}</div>
              </div>
            </div>
          </div>
          <div class="guide-tip">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span>如需开通直播权限，请前往「个人中心 - 主播招募」申请</span>
          </div>
          <div class="obs-settings">
            <h4>OBS 关键设置</h4>
            <ol>
              <li>在主播中心点击“开始直播”，让房间进入“等待 OBS 推流”。</li>
              <li>打开 OBS → 设置 → 直播，服务选择“自定义”。</li>
              <li>把主播中心显示的 RTMP 地址填入“服务器”，串流密钥填入“串流密钥”。密钥属于私密凭证，不要截图或转发。</li>
              <li>添加显示器采集、窗口采集或视频采集设备，并确认桌面音频和麦克风音量条有变化。</li>
              <li>点击 OBS“开始直播”；主播中心识别到 SRS 推流后会从“等待推流”自动变为“直播中”。</li>
              <li>结束时先在 OBS 停止直播，再回主播中心点击“停止直播”。</li>
            </ol>
          </div>
        </div>

        <!-- 礼物说明 -->
        <div v-if="currentTab === 'gift' && !minorMode" class="content-section">
          <h2 class="content-title">礼物说明</h2>
          <div class="gift-table">
            <div class="gift-row gift-header">
              <div class="gift-col col-name">礼物名称</div>
              <div class="gift-col col-price">价格</div>
              <div class="gift-col col-desc">说明</div>
            </div>
            <div class="gift-row" v-for="(gift, i) in giftList" :key="i">
              <div class="gift-col col-name">{{ gift.name }}</div>
              <div class="gift-col col-price">{{ gift.price }}</div>
              <div class="gift-col col-desc">{{ gift.desc }}</div>
            </div>
          </div>
          <div class="gift-note">
            <h4>送礼规则</h4>
            <ul>
              <li>礼物按账户余额直接扣费，发送前请确认礼物和数量</li>
              <li>主播按礼物实付金额的 50% 获得可提现收益</li>
              <li>收益可在「个人中心 - 我的佣金」查看并提交提现申请</li>
              <li>提现申请需经平台审核；页面中的“已打款”表示后台已确认完成支付</li>
            </ul>
          </div>
        </div>

        <!-- 充值帮助 -->
        <div v-if="currentTab === 'recharge' && !minorMode" class="content-section">
          <h2 class="content-title">充值帮助</h2>
          <div class="recharge-content">
            <div class="recharge-block">
              <h4>充值方式</h4>
              <p>目前支持支付宝扫码支付，充值成功后余额将到账。</p>
              <ol>
                <li>进入「充值中心」，选择套餐后点击「立即充值」</li>
                <li>使用支付宝扫描页面弹出的收款二维码完成支付</li>
                <li>支付完成后截图保存凭证，联系客服确认，余额将人工到账</li>
              </ol>
            </div>
            <div class="recharge-block">
              <h4>充值比例</h4>
              <p>按 1 元 = 10 余额的比例充值，不同套餐赠送的余额不同，例如：</p>
              <ul>
                <li>6 元：60 余额</li>
                <li>12 元：120 余额 + 赠送 6 余额</li>
                <li>30 元：300 余额 + 赠送 30 余额</li>
                <li>68 元：680 余额 + 赠送 88 余额</li>
                <li>128 元：1280 余额 + 赠送 198 余额</li>
                <li>328 元：3280 余额 + 赠送 588 余额</li>
              </ul>
            </div>
            <div class="recharge-block">
              <h4>充值未到账</h4>
              <p>如支付成功但余额未到账，请按以下步骤排查：</p>
              <ol>
                <li>确认支付宝已完成扣款</li>
                <li>联系客服并提供支付截图，确认后人工到账</li>
              </ol>
            </div>
            <div class="recharge-block">
              <h4>退款说明</h4>
              <p>虚拟商品一经购买不予退款，请确认后再进行充值操作。如遇特殊情况，请联系客服处理。</p>
            </div>
            <div class="recharge-block">
              <h4>联系客服</h4>
              <p>电话：13691135537</p>
              <p>邮箱：contact@lingmouyuntu.com</p>
            </div>
          </div>
        </div>
      </main>
    </div>
    <PageFooter />
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import PageFooter from './Footer.vue';
import { getGiftList } from '../api/liveGift';
import { getRealnameStatus } from '../api/realname';

export default {
  name: 'HelpCenter',
  components: { PageFooter },
  setup() {
    const route = useRoute();
    const currentTab = ref(route.query.tab || 'faq');

    watch(() => route.query.tab, (newTab) => {
      if (newTab) currentTab.value = newTab;
    });

    // 青少年模式：礼物/充值说明含套餐价目与支付引导，等同向未成年人推广消费，
    // 需从导航中移除，且直接访问 URL 也不能展示内容。
    const minorMode = ref(false);
    const minorRestricted = ['gift', 'recharge'];
    const fetchMinorMode = () => {
      getRealnameStatus().then(res => {
        const d = res && (res.data || res);
        minorMode.value = !!(d && Number(d.minor_mode) === 1);
        // 已停留在受限 tab 上（直接访问 URL 或开关变更）：退回常见问题
        if (minorMode.value && minorRestricted.indexOf(currentTab.value) >= 0) {
          currentTab.value = 'faq';
        }
      }).catch(() => { /* 异常时保持原样，不影响正常用户 */ });
    };

    const allNavItems = [
      { key: 'faq', text: '常见问题', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>' },
      { key: 'live', text: '直播指南', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="18" height="12" rx="3"/><circle cx="11" cy="12" r="3" fill="currentColor"/><path d="M22 9v6"/></svg>' },
      { key: 'gift', text: '礼物说明', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>' },
      { key: 'recharge', text: '充值帮助', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/></svg>' }
    ];
    const navItems = computed(() => minorMode.value
      ? allNavItems.filter(i => minorRestricted.indexOf(i.key) < 0)
      : allNavItems);

    const faqList = ref([
      { q: '如何注册灵眸云图账号？', a: '点击页面右上角的「注册」按钮，填写用户名、密码和邮箱即可完成注册。注册成功后可直接登录使用。', open: true },
      { q: '忘记密码怎么办？', a: '在登录页面点击「忘记密码」，输入注册邮箱，系统将发送密码重置链接到您的邮箱，按指引操作即可重设密码。', open: false },
      { q: '如何关注喜欢的主播？', a: '进入主播的直播间或个人主页，点击「关注」按钮即可。关注后可在「个人中心 - 关注与历史」中查看已关注的主播。', open: false },
      { q: '如何修改昵称和头像？', a: '进入「个人中心 - 我的信息」，可以修改昵称、上传新头像。头像支持 JPG、PNG 格式，大小不超过 2MB。', open: false },
      { q: '贵族特权有哪些？', a: '贵族共分6个等级：勋爵、男爵、子爵、伯爵、公爵、国王。等级越高享受的特权越多，包括专属徽章、进场特效、礼物折扣、房管权限等。', open: false },
      { q: '如何成为主播？', a: '前往「个人中心 - 主播招募」页面提交申请，填写真实信息后等待审核。审核通过后即可在「开播」页面开始直播。', open: false },
      { q: '直播间被禁言了怎么办？', a: '直播间禁言由房主或房管操作，如认为被误禁言，可联系该直播间的房主或房管申请解除。永久禁言需联系平台客服申诉。', open: false },
      { q: '如何注销账号？', a: '请联系平台客服提交注销申请，客服核实身份后将为您处理。注销后账号数据将无法恢复，请谨慎操作。', open: false }
    ]);

    const liveSteps = [
      { title: '申请主播权限', desc: '在「个人中心 - 主播招募」页面提交申请，等待平台审核通过' },
      { title: '安装 OBS', desc: '从 OBS 官方网站下载 Windows 版，完成安装并首次启动' },
      { title: '创建直播间', desc: '进入主播中心，填写标题、上传 16:9 封面并创建直播间' },
      { title: '准备开播', desc: '填写本场标题和封面，点击“开始直播”进入等待推流状态' },
      { title: '配置 OBS', desc: '服务选择“自定义”，复制主播中心的 RTMP 地址和串流密钥' },
      { title: '真实推流', desc: '在 OBS 添加画面和声音来源，点击“开始直播”，等待页面自动识别' },
      { title: '互动管理', desc: '直播过程中可以查看弹幕、管理房管、禁言违规用户等' },
      { title: '结束直播', desc: '先停止 OBS 推流，再在主播中心停止本场直播' }
    ];

    const giftList = ref([]);
    onMounted(async () => {
      fetchMinorMode();
      window.addEventListener('minor-mode-changed', fetchMinorMode);
      try {
        const res = await getGiftList();
        giftList.value = (res.data || []).map(gift => ({
          name: gift.name,
          price: `¥${Number(gift.price).toFixed(2)}`,
          desc: gift.description || '直播间互动礼物'
        }));
      } catch (e) { giftList.value = []; }
    });
    onBeforeUnmount(() => {
      window.removeEventListener('minor-mode-changed', fetchMinorMode);
    });

    // 直接改 URL query 切到受限 tab 时也要拦住
    watch(currentTab, (t) => {
      if (minorMode.value && minorRestricted.indexOf(t) >= 0) {
        currentTab.value = 'faq';
      }
    });

    const toggleFaq = (i) => {
      faqList.value[i].open = !faqList.value[i].open;
    };

    return { currentTab, navItems, faqList, liveSteps, giftList, toggleFaq, minorMode };
  }
};
</script>

<style scoped>
.help-center {
  min-height: 100vh;
  background: #f5f5f7;
}
.help-container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 32px 20px;
  display: flex;
  gap: 24px;
}

/* 侧边导航 */
.help-sidebar {
  width: 200px;
  flex-shrink: 0;
}
.sidebar-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  padding: 0 14px;
}
.sidebar-nav {
  background: #fff;
  border-radius: 12px;
  padding: 8px;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s;
}
.nav-item:hover {
  background: #f8f8f8;
  color: #333;
}
.nav-item.active {
  background: linear-gradient(90deg, #fff5eb, #fff);
  color: #ff6a00;
  font-weight: 600;
}
.nav-icon {
  display: flex;
  align-items: center;
}

/* 内容区 */
.help-content {
  flex: 1;
  min-width: 0;
}
.content-title {
  font-size: 22px;
  font-weight: 700;
  color: #333;
  margin: 0 0 24px;
}
.content-section {
  background: #fff;
  border-radius: 12px;
  padding: 32px;
}

/* FAQ */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.faq-item {
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.2s;
}
.faq-item:hover {
  border-color: #ffe4cc;
}
.faq-q {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}
.faq-q-text {
  flex: 1;
}
.faq-arrow {
  display: flex;
  align-items: center;
  color: #ccc;
  transition: transform 0.25s;
}
.faq-arrow.open {
  transform: rotate(180deg);
}
.faq-a {
  padding: 0 18px 16px;
  font-size: 14px;
  color: #666;
  line-height: 1.7;
}

/* 直播指南 */
.obs-download-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
  padding: 18px 20px;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: linear-gradient(135deg, #eff6ff, #fff);
}
.obs-download-card strong, .obs-download-card span { display: block; }
.obs-download-card strong { color: #1e293b; margin-bottom: 4px; }
.obs-download-card span { color: #64748b; font-size: 13px; }
.obs-download-card a { flex-shrink: 0; padding: 10px 16px; border-radius: 7px; background: #2563eb; color: #fff; text-decoration: none; font-size: 13px; font-weight: 700; }
.guide-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}
.guide-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px;
  background: #fafafa;
  border-radius: 10px;
  transition: background 0.2s;
}
.guide-card:hover {
  background: #fff8f0;
}
.guide-num {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.guide-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
}
.guide-desc {
  font-size: 13px;
  color: #888;
}
.guide-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  background: #fff8f0;
  border: 1px solid #ffe4cc;
  border-radius: 10px;
  font-size: 13px;
  color: #ff6a00;
}
.obs-settings { margin-top: 20px; padding: 20px; border-radius: 10px; background: #fafafa; }
.obs-settings h4 { margin: 0 0 12px; color: #333; }
.obs-settings ol { margin: 0; padding-left: 20px; }
.obs-settings li { margin: 7px 0; color: #5f6368; font-size: 13px; line-height: 1.7; }

/* 礼物说明 */
.gift-table {
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 24px;
}
.gift-row {
  display: flex;
  padding: 14px 18px;
  border-bottom: 1px solid #f5f5f5;
}
.gift-row:last-child {
  border-bottom: none;
}
.gift-header {
  background: #fafafa;
  font-weight: 700;
  color: #333;
  font-size: 14px;
}
.gift-col {
  padding: 0 8px;
}
.col-name { width: 120px; }
.col-price { width: 100px; color: #ff6a00; font-weight: 600; }
.col-desc { flex: 1; color: #666; }
.gift-header .col-price { color: #333; }
.gift-header .col-desc { color: #333; }
.gift-note {
  background: #fafafa;
  border-radius: 10px;
  padding: 20px;
}
.gift-note h4 {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin: 0 0 12px;
}
.gift-note ul {
  margin: 0;
  padding-left: 18px;
}
.gift-note li {
  font-size: 13px;
  color: #666;
  line-height: 2;
}

/* 充值帮助 */
.recharge-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.recharge-block h4 {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin: 0 0 10px;
  padding-left: 10px;
  border-left: 3px solid #ff9600;
}
.recharge-block p {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
  margin: 0;
}
.recharge-block ol {
  padding-left: 20px;
}
.recharge-block ol li {
  font-size: 14px;
  color: #666;
  line-height: 2;
}

@media (max-width: 768px) {
  .help-container {
    flex-direction: column;
    padding: 16px;
  }
  .help-sidebar {
    width: 100%;
  }
  .sidebar-nav {
    display: flex;
    overflow-x: auto;
  }
  .nav-item {
    flex-shrink: 0;
  }
  .content-section {
    padding: 20px;
  }
  .gift-row {
    font-size: 13px;
  }
  .col-name { width: 80px; }
  .col-price { width: 70px; }
}
</style>
